// Форма своего упражнения или правки любого: название, мышцы, оборудование, тип учёта, Garmin, техника.

import { useMutation } from '@tanstack/react-query'
import { useId, useState, type FormEvent } from 'react'

import { ApiError } from '@/api/client'
import { Button, controlClass, ErrorText, Field, Input, Modal, Select } from '@/components/ui'
import { cn } from '@/lib/cn'
import {
  trainingApi,
  useInvalidateTraining,
  useMuscles,
  type Equipment,
  type Exercise,
  type ExerciseInput,
  type Tracking,
} from '@/pages/workouts/api'
import { EQUIPMENT_LABELS, TRACKING_LABELS } from '@/pages/workouts/logic'

import { MuscleSelect } from './ExercisePicker'

function initial(exercise: Exercise | null): ExerciseInput {
  return {
    name: exercise?.name ?? '',
    primary_muscle: exercise?.primary_muscle ?? '',
    secondary_muscles: exercise?.secondary_muscles ?? [],
    equipment: exercise?.equipment ?? 'barbell',
    tracking: exercise?.tracking ?? 'weight_reps',
    garmin_category: exercise?.garmin_category ?? null,
    garmin_name: exercise?.garmin_name ?? null,
    notes: exercise?.notes ?? null,
    description: exercise?.description ?? null,
  }
}

export function ExerciseForm({
  open,
  onClose,
  exercise,
  onSaved,
}: {
  open: boolean
  onClose: () => void
  /** null — новое упражнение. */
  exercise: Exercise | null
  onSaved?: (exercise: Exercise) => void
}) {
  const [form, setForm] = useState<ExerciseInput>(() => initial(exercise))
  const techniqueId = useId()
  const muscles = useMuscles()
  const invalidate = useInvalidateTraining()
  const save = useMutation({
    mutationFn: (data: ExerciseInput) =>
      exercise ? trainingApi.updateExercise(exercise.id, data) : trainingApi.createExercise(data),
    onSuccess: async (saved) => {
      await invalidate()
      onSaved?.(saved)
      onClose()
    },
  })
  const fieldError = (field: string) =>
    save.error instanceof ApiError && save.error.field === field ? save.error.message : null
  const set = <K extends keyof ExerciseInput>(key: K, value: ExerciseInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  const submit = (e: FormEvent) => {
    e.preventDefault()
    save.mutate({
      ...form,
      name: form.name.trim(),
      garmin_category: form.garmin_category?.trim() || null,
      garmin_name: form.garmin_name?.trim() || null,
      notes: form.notes?.trim() || null,
      description: form.description?.trim() || null,
    })
  }

  const toggleSecondary = (key: string) =>
    set(
      'secondary_muscles',
      form.secondary_muscles.includes(key)
        ? form.secondary_muscles.filter((m) => m !== key)
        : [...form.secondary_muscles, key],
    )

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={exercise ? 'Изменить упражнение' : 'Своё упражнение'}
      className="sm:max-w-lg"
      footer={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button variant="primary" type="submit" form="exercise-form" loading={save.isPending}>
            Сохранить
          </Button>
        </>
      }
    >
      <form id="exercise-form" onSubmit={submit} className="grid gap-4" noValidate>
        <Input
          label="Название"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          error={fieldError('name')}
          required
        />
        <MuscleSelect
          label="Основная группа мышц"
          placeholder="Выбери группу"
          value={form.primary_muscle}
          onChange={(v) => set('primary_muscle', v)}
        />
        {fieldError('primary_muscle') ? <ErrorText>{fieldError('primary_muscle')}</ErrorText> : null}
        <fieldset>
          <legend className="mb-1.5 text-sm font-medium text-fg">Вспомогательные</legend>
          <div className="flex flex-wrap gap-1.5">
            {(muscles.data ?? [])
              .filter((m) => m.key !== form.primary_muscle)
              .map((m) => {
                const on = form.secondary_muscles.includes(m.key)
                return (
                  <button
                    key={m.key}
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggleSecondary(m.key)}
                    className={
                      on
                        ? 'min-h-9 rounded-md bg-accent-soft px-2.5 text-sm text-accent'
                        : 'min-h-9 rounded-md bg-surface-2 px-2.5 text-sm text-muted hover:text-fg'
                    }
                  >
                    {m.label_ru}
                  </button>
                )
              })}
          </div>
        </fieldset>
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Оборудование"
            value={form.equipment}
            onChange={(e) => set('equipment', e.target.value as Equipment)}
            options={Object.entries(EQUIPMENT_LABELS).map(([value, label]) => ({ value, label }))}
          />
          <Select
            label="Что записывать"
            value={form.tracking}
            onChange={(e) => set('tracking', e.target.value as Tracking)}
            options={Object.entries(TRACKING_LABELS).map(([value, label]) => ({ value, label }))}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Garmin: категория"
            hint="Например, BENCH_PRESS"
            value={form.garmin_category ?? ''}
            onChange={(e) => set('garmin_category', e.target.value)}
          />
          <Input
            label="Garmin: упражнение"
            hint="Например, BARBELL_BENCH_PRESS"
            value={form.garmin_name ?? ''}
            onChange={(e) => set('garmin_name', e.target.value)}
          />
        </div>
        <Input label="Заметка" value={form.notes ?? ''} onChange={(e) => set('notes', e.target.value)} />
        <Field id={techniqueId} label="Техника" hint="Шаг на строку — так они покажутся в «Инфо» на тренировке">
          <textarea
            id={techniqueId}
            rows={5}
            value={form.description ?? ''}
            onChange={(e) => set('description', e.target.value)}
            className={cn(controlClass, 'py-2 leading-snug')}
          />
        </Field>
        {save.error && !fieldError('name') && !fieldError('primary_muscle') ? <ErrorText error={save.error} /> : null}
      </form>
    </Modal>
  )
}
